import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  favoriteProduct,
  fetchFilteredProductReviews,
  fetchLtoItem,
  fetchProduct,
  fetchBottomLine,
  createProductReview,
  uploadReviewImage,
  unFavoriteProduct,
  fetchProductRecommendations,
} from './product.api';
import { CreateReviewParams } from '../../models/product.types';

export const useProductItemQuery = (identifier: number | string) => useQuery({
  queryKey: ['productItem', identifier],
  queryFn: () => fetchProduct(identifier),
  enabled: !!identifier,
});

export const useProductRecommendationsQuery = (identifier: number | string,
  isLoggedIn: boolean) => useQuery({
  queryKey: ['productRecommendations', identifier, isLoggedIn],
  queryFn: () => fetchProductRecommendations(identifier, !isLoggedIn),
  enabled: !!identifier,
});

export const clearProductCache = (queryClient, identifier: number | string) => {
  queryClient.invalidateQueries({ queryKey: ['productItem', identifier] });
};

export const useLtoItemQuery = (identifier: number | string) => useQuery({
  queryKey: ['ltoItem', identifier],
  queryFn: () => fetchLtoItem(identifier),
  enabled: !!identifier,
});

export const useFavoriteProduct = (cartId: number, menuId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: number) => favoriteProduct(productId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['cartItems', cartId] });
      queryClient.invalidateQueries({ queryKey: ['menuItems', menuId, true] });
      return response;
    },
  });
};

export const useFilteredProductReviewsQuery = (
  productId: number | undefined,
  perPage = 6,
  page = 1,
  scores?: number[],
  sort = 'date',
  direction = 'desc',
  pictured?: boolean,
) => useQuery({
  queryKey: ['filteredProductReviews', productId, perPage, page, scores, sort, direction, pictured],
  queryFn: () => fetchFilteredProductReviews(productId!,
    perPage, page, scores, sort, direction, pictured),
  enabled: !!productId,
});

export const useProductReviewsQuery = (
  productId: number | undefined,
) => useQuery({
  queryKey: ['productReviews', productId],
  queryFn: () => fetchBottomLine(productId!),
  enabled: !!productId,
});

export const useCreateProductReview = () => useMutation({
  mutationFn: ({
    productId,
    ...params
  }: { productId: number } & CreateReviewParams) => createProductReview(productId, params),
});

export const useUploadReviewImage = () => useMutation({
  mutationFn: ({
    productId,
    imageUploadToken,
    image,
  }: {
    productId: number;
    imageUploadToken: string; image: File
  }) => uploadReviewImage(productId, imageUploadToken, image),
});

export const useUnFavoriteProduct = (cartId: number, menuId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: number) => unFavoriteProduct(productId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['cartItems', cartId] });
      queryClient.invalidateQueries({ queryKey: ['menuItems', menuId, true] });
      return response;
    },
  });
};
